import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { CareGiver } from "../types/customer";
import { elevation, colors, typography } from "../theme";
import { NextOfKinDialog } from "./dialogs/NextOfKinDialog";
import ErrorIcon from "@mui/icons-material/Error";

interface NextOfKinSectionProps {
  careGivers: CareGiver[];
  validationErrors?: { [key: string]: string | undefined }; // Flat structure like careGivers[0].FirstName
  disabled?: boolean;
  onAddCareGiver: (caregiver: Omit<CareGiver, "relationshipId">) => void;
  onUpdateCareGiver: (caregiver: CareGiver) => void;
  onDeleteCareGiver: (caregiverId: string) => void;
}

export const NextOfKinSection: React.FC<NextOfKinSectionProps> = ({
  careGivers,
  validationErrors,
  disabled = false,
  onAddCareGiver,
  onUpdateCareGiver,
  onDeleteCareGiver,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCareGiver, setSelectedCareGiver] = useState<CareGiver | null>(
    null
  );
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newDialogOpen, setNewDialogOpen] = useState(false);

  const handleDelete = (caregiver: CareGiver) => {
    setSelectedCareGiver(caregiver);
    setDeleteDialogOpen(true);
  };

  const handleEdit = (caregiver: CareGiver) => {
    setSelectedCareGiver(caregiver);
    setEditDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedCareGiver?.relationshipId) {
      onDeleteCareGiver(selectedCareGiver.relationshipId.toString());
      setDeleteDialogOpen(false);
      setSelectedCareGiver(null);
    }
  };

  const handleNewClick = () => {
    setNewDialogOpen(true);
  };

  return (
    <Paper sx={{ p: 3, height: "100%" }} elevation={elevation.paper}>
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6">Next of Kin</Typography>
          <Tooltip title="Add Next of Kin">
            <IconButton onClick={handleNewClick} size="small" disabled={disabled}>
              <AddCircleOutlineIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          overflowX: "auto",
          pb: 2,
          "&::-webkit-scrollbar": {
            height: 8,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: colors.background.default,
            borderRadius: 4,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: colors.background.sidebarGlass,
            borderRadius: 4,
          },
        }}
      >
        {careGivers.map((caregiver, index) => {
          // Find all validation errors that match careGivers[index].*
          const errorPattern = new RegExp(`^CareGivers\\[${index}\\]\\.(.+)$`);
          const errorMessages = Object.entries(validationErrors || {})
            .filter(([key]) => errorPattern.test(key))
            .map(([_, value]) => value as string)
            .filter(Boolean);

          const hasErrors = errorMessages.length > 0;

          return (
            <Card
              key={caregiver.relationshipId}
              sx={{
                width: 300,
                maxWidth: 300,
                p: 2,
                borderRadius: 1,
                flex: "0 0 300px",
                position: "relative",
                border: hasErrors
                  ? `2px solid ${colors.status.error}`
                  : undefined,
              }}
            >
              {hasErrors && (
                <Tooltip
                  title={
                    <Box>
                      {errorMessages.map((error, i) => (
                        <Typography key={i} variant="body2" sx={{ py: 0.5 }}>
                          {error}
                        </Typography>
                      ))}
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: -8,
                      right: -8,
                      backgroundColor: "#fff",
                      borderRadius: "50%",
                      padding: 0.5,
                      border: `2px solid ${colors.status.error}`,
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      zIndex: 1,
                      cursor: "pointer",
                      "&:hover": {
                        transform: "scale(1.1)",
                        boxShadow: "0 3px 6px rgba(0,0,0,0.15)",
                      },
                      transition: "all 0.2s ease-in-out",
                    }}
                  >
                    <ErrorIcon
                      fontSize="small"
                      sx={{
                        color: colors.status.error,
                        display: "block",
                      }}
                    />
                  </Box>
                </Tooltip>
              )}
              <Box
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  display: "flex",
                  gap: 0.5,
                }}
              >
                <IconButton size="small" onClick={() => handleEdit(caregiver)} disabled={disabled}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(caregiver)}
                  sx={{ "&:hover": { color: "error.main" } }}
                  disabled={disabled}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>

              <Typography
                sx={{
                  fontWeight: typography.fontWeight.medium,
                  fontSize: typography.fontSize.large,
                  mb: 0.5,
                  pr: 7, // Make room for the buttons
                }}
              >
                {caregiver.firstName} {caregiver.lastName}{" "}
                <Typography component="span" color="text.secondary">
                  ({caregiver.relationshipType})
                </Typography>
              </Typography>

              <Typography
                color="text.secondary"
                sx={{ fontSize: typography.fontSize.small }}
              >
                {caregiver.email}
              </Typography>

              <Typography
                color="text.secondary"
                sx={{ fontSize: typography.fontSize.small }}
              >
                {caregiver.phones.find((p) => p.isPrimary)?.number ||
                  caregiver.phones[0]?.number}
              </Typography>

              <Typography
                color="text.secondary"
                sx={{
                  fontSize: typography.fontSize.small,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={
                  caregiver.phones.find((p) => p.isPrimary)?.note ||
                  caregiver.phones[0]?.note
                }
              >
                {caregiver.phones.find((p) => p.isPrimary)?.note ||
                  caregiver.phones[0]?.note ||
                  ""}
              </Typography>

              <Typography
                color="text.secondary"
                sx={{ fontSize: typography.fontSize.small }}
              >
                {caregiver.addresses.find((a) => a.isPrimary)?.postcode ||
                  caregiver.addresses[0]?.postcode}
              </Typography>
            </Card>
          );
        })}
      </Box>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Delete Next of Kin</DialogTitle>
        <DialogContent>
          Are you sure you want to delete {selectedCareGiver?.firstName}{" "}
          {selectedCareGiver?.lastName} as next of kin?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* New NOK Dialog */}
      <NextOfKinDialog
        open={newDialogOpen}
        onClose={() => setNewDialogOpen(false)}
        onSubmit={onAddCareGiver}
      />

      {/* Edit NOK Dialog */}
      <NextOfKinDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedCareGiver(null);
        }}
        caregiver={selectedCareGiver || undefined}
        onSubmit={(caregiver) => onUpdateCareGiver(caregiver as CareGiver)}
      />
    </Paper>
  );
};
